import { useState, useMemo } from 'react'
import { Card, CardType, Faction, Rarity } from '../../types'
import { useAppStore } from '../../store'
import { Search, Filter, ChevronDown } from 'lucide-react'

interface CardListProps {
  onSelectCard: (card: Card) => void
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

const getImageUrl = (localImage?: string, fallbackUrl?: string) => {
  if (localImage) {
    return localImage.startsWith('http') ? localImage : `${API_URL}/${localImage}`
  }
  return fallbackUrl
}

const CARD_TYPE_LABELS: Record<CardType, string> = {
  [CardType.UNIT]: 'Unidad',
  [CardType.SPELL]: 'Hechizo',
  [CardType.ARTIFACT]: 'Artefacto',
  [CardType.LEADER]: 'Líder',
}

const FACTION_LABELS: Record<Faction, string> = {
  [Faction.HUMAN]: 'Humano',
  [Faction.DEMON]: 'Demonio',
  [Faction.ELF]: 'Elfo',
  [Faction.DRAGON]: 'Dragón',
  [Faction.UNDEAD]: 'No-muerto',
  [Faction.NEUTRAL]: 'Neutral',
}

const RARITY_LABELS: Record<Rarity, string> = {
  [Rarity.COMMON]: 'Común',
  [Rarity.UNCOMMON]: 'Poco Común',
  [Rarity.RARE]: 'Rara',
  [Rarity.LEGENDARY]: 'Legendaria',
  [Rarity.MYTHIC]: 'Mítica',
}

export function CardList({ onSelectCard }: CardListProps) {
  const storeCards = useAppStore((state) => state.cards)
  const cards = Array.isArray(storeCards) ? storeCards : []
  const cardFilters = useAppStore((state) => state.cardFilters)
  
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<CardType | 'ALL'>('ALL')
  const [factionFilter, setFactionFilter] = useState<Faction | 'ALL'>('ALL')
  const [rarityFilter, setRarityFilter] = useState<Rarity | 'ALL'>('ALL')
  const [costFilter, setCostFilter] = useState({ min: 0, max: 10 })
  const [showFilters, setShowFilters] = useState(false)

  const filteredCards = useMemo(() => {
    return cards.filter((card) => {
      const matchesSearch =
        search === '' ||
        card.name.toLowerCase().includes(search.toLowerCase()) ||
        (card.cardId && card.cardId.toLowerCase().includes(search.toLowerCase()))
      const matchesType = typeFilter === 'ALL' || card.type === typeFilter
      const matchesFaction = factionFilter === 'ALL' || card.faction === factionFilter
      const matchesRarity = rarityFilter === 'ALL' || card.rarity === rarityFilter
      const matchesCost = card.cost >= costFilter.min && card.cost <= costFilter.max

      return matchesSearch && matchesType && matchesFaction && matchesRarity && matchesCost
    })
  }, [cards, search, typeFilter, factionFilter, rarityFilter, costFilter])

  const costOptions = Array.from({ length: 11 }, (_, i) => i)

  return (
    <div className="flex flex-col h-full bg-gray-900">
      <div className="p-4 border-b border-gray-700">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar cartas..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="mt-3 flex items-center gap-2 px-3 py-2 bg-gray-800 rounded-lg text-gray-300 hover:bg-gray-700 text-sm"
        >
          <Filter className="w-4 h-4" />
          Filtros
          <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {showFilters && (
        <div className="p-4 border-b border-gray-700 bg-gray-850 space-y-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Tipo</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as CardType | 'ALL')}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm"
            >
              <option value="ALL">Todos</option>
              {Object.entries(CARD_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">FacciÃ³n</label>
            <select
              value={factionFilter}
              onChange={(e) => setFactionFilter(e.target.value as Faction | 'ALL')}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm"
            >
              <option value="ALL">Todas</option>
              {Object.entries(FACTION_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Rareza</label>
            <select
              value={rarityFilter}
              onChange={(e) => setRarityFilter(e.target.value as Rarity | 'ALL')}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm"
            >
              <option value="ALL">Todas</option>
              {Object.entries(RARITY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Costo: {costFilter.min} - {costFilter.max}</label>
            <div className="flex gap-2">
              <select
                value={costFilter.min}
                onChange={(e) => setCostFilter({ ...costFilter, min: Number(e.target.value) })}
                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm"
              >
                {costOptions.map((c) => (
                  <option key={`min-${c}`} value={c}>{c}</option>
                ))}
              </select>
              <select
                value={costFilter.max}
                onChange={(e) => setCostFilter({ ...costFilter, max: Number(e.target.value) })}
                className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm"
              >
                {costOptions.map((c) => (
                  <option key={`max-${c}`} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-2">
        <p className="text-xs text-gray-500 mb-2">{filteredCards.length} cartas</p>
        <div className="grid grid-cols-2 gap-2">
          {filteredCards.slice(0, 100).map((card) => (
            <button
              key={card.id}
              onClick={() => onSelectCard(card)}
              className="relative group bg-gray-800 rounded-lg overflow-hidden hover:ring-2 hover:ring-blue-500 transition-all"
            >
              {(card as Card & { localImage?: string }).localImage ? (
                  <img
                    src={getImageUrl((card as Card & { localImage?: string }).localImage)}
                    alt={card.name}
                    className="w-full aspect-[2/3] object-cover"
                  />
                ) : card.imageUrl ? (
                  <img
                    src={card.imageUrl}
                    alt={card.name}
                    className="w-full aspect-[2/3] object-cover"
                  />
                ) : (
                  <img
                    src="/src/assets/cards/back_myl.png"
                    alt="Back"
                    className="w-full aspect-[2/3] object-contain bg-[#1a1e24]"
                  />
                )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute bottom-0 left-0 right-0 p-2">
                  <p className="text-white text-xs font-medium truncate">{card.name}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-gray-300 text-xs">{card.faction}</span>
                    <div className="flex gap-1">
                      {card.goldCost !== undefined && card.goldCost !== null && (
                        <span className="bg-yellow-600 text-white text-xs px-1.5 py-0.5 rounded">
                          💰{card.goldCost}
                        </span>
                      )}
                      <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded">
                        {card.cost}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute top-1 right-1 bg-gray-900/80 text-white text-xs px-1.5 py-0.5 rounded">
                {card.rarity === Rarity.LEGENDARY || card.rarity === Rarity.MYTHIC ? '★' : ''}
              </div>
            </button>
          ))}
        </div>
        {filteredCards.length > 100 && (
          <p className="text-center text-gray-500 text-sm mt-4">
            Mostrando 100 de {filteredCards.length} cartas
          </p>
        )}
      </div>
    </div>
  )
}
